/**
 * Metrc Globals - Ingredient, Cannabinoid, and Terpene Metadata
 *
 * Static metadata dictionaries used for label templates.
 * Source: retailid/packages/lib/metrc/ingredients.ts
 */

import type { MetrcGlobals } from './index';

// ============================================================================
// Types
// ============================================================================

export interface IngredientMeta {
  label: string;
  noGreekLabel?: string;
  isCannabinoid?: boolean;
  isTerpene?: boolean;
  isThc?: boolean;
  isCbd?: boolean;
  isDerivative?: boolean;
  alpha?: number;
  color?: string;
}

// ============================================================================
// Cannabinoids
// ============================================================================

const cannabinoidsMeta: Record<string, IngredientMeta> = {
  activeThc: { isCannabinoid: true, isDerivative: true, isThc: true, label: "Active THC", noGreekLabel: "Active THC" },
  activeCbd: { isCannabinoid: true, isDerivative: true, isCbd: true, label: "Active CBD", noGreekLabel: "Active CBD" },
  exothc: { isCannabinoid: true, isThc: true, label: "exo-THC", noGreekLabel: "exo-THC" },
  delta9thc: { isCannabinoid: true, isThc: true, label: "Δ9 THC", noGreekLabel: "Delta-9 THC" },
  delta9thca: { alpha: 0.877, isCannabinoid: true, isThc: true, label: "Δ9 THCa", noGreekLabel: "Delta-9 THCa" },
  delta8thc: { isCannabinoid: true, isThc: true, label: "Δ8 THC", noGreekLabel: "Delta-8 THC" },
  delta8thca: { alpha: 0.877, isCannabinoid: true, isThc: true, label: "Δ8 THCa", noGreekLabel: "Delta-8 THCa" },
  delta10thc: { isCannabinoid: true, isThc: true, label: "Δ10 THC", noGreekLabel: "Delta-10 THC" },
  delta10thca: { alpha: 0.877, isCannabinoid: true, isThc: true, label: "Δ10 THCa", noGreekLabel: "Delta-10 THCa" },
  thc: { isCannabinoid: true, isThc: true, label: "Δ9 THC (deprecated)", noGreekLabel: "Delta-9 THC (deprecated)" },
  thca: { alpha: 0.877, isCannabinoid: true, isThc: true, label: "THCa", noGreekLabel: "THCa" },
  thcv: { isCannabinoid: true, isThc: true, label: "THCV", noGreekLabel: "THCV" },
  thcva: { isCannabinoid: true, isThc: true, alpha: 0.877, label: "THCVa", noGreekLabel: "THCVa" },
  cbc: { isCannabinoid: true, label: "CBC", noGreekLabel: "CBC" },
  cbca: { isCannabinoid: true, label: "CBCa", noGreekLabel: "CBCa" },
  cbd: { isCannabinoid: true, isCbd: true, label: "CBD", noGreekLabel: "CBD" },
  cbda: { alpha: 0.877, isCannabinoid: true, isCbd: true, label: "CBDa", noGreekLabel: "CBDa" },
  cbdv: { isCannabinoid: true, isCbd: true, label: "CBDV", noGreekLabel: "CBDV" },
  cbdva: { alpha: 0.877, isCannabinoid: true, isCbd: true, label: "CBDVa", noGreekLabel: "CBDVa" },
  cbg: { isCannabinoid: true, label: "CBG", noGreekLabel: "CBG" },
  cbga: { alpha: 0.877, isCannabinoid: true, label: "CBGa", noGreekLabel: "CBGa" },
  cbn: { isCannabinoid: true, label: "CBN", noGreekLabel: "CBN" },
  cbna: { alpha: 0.877, isCannabinoid: true, label: "CBNa", noGreekLabel: "CBNa" },
  cbt: { isCannabinoid: true, label: "CBT", noGreekLabel: "CBT" },
  totalDelta9thc: { isCannabinoid: true, isDerivative: true, isThc: true, label: "Total Δ9 THC", noGreekLabel: "Total Delta-9 THC" },
  totalThc: { isCannabinoid: true, isDerivative: true, isThc: true, label: "Total THC", noGreekLabel: "Total THC" },
  totalCbd: { isCannabinoid: true, isCbd: true, isDerivative: true, label: "Total CBD", noGreekLabel: "Total CBD" },
  totalCannabinoids: { isCannabinoid: true, isDerivative: true, label: "Total Cannabinoids", noGreekLabel: "Total Cannabinoids" },
  totalActiveCannabinoids: { isCannabinoid: true, isDerivative: true, label: "Total Active Cannabinoids", noGreekLabel: "Total Active Cannabinoids" },
};

// ============================================================================
// Terpenes
// ============================================================================

const terpenesMeta: Record<string, IngredientMeta> = {
  aBisabolol: { isTerpene: true, label: "α-Bisabolol", noGreekLabel: "Alpha-Bisabolol", color: "F2EDDF" },
  aFarnesene: { isTerpene: true, label: "α-Farnesene", noGreekLabel: "Alpha-Farnesene", color: "FB58D1" },
  aHumulene: { isTerpene: true, label: "α-Humulene", noGreekLabel: "Alpha-Humulene", color: "78081C" },
  aLimonene: { isTerpene: true, label: "α-Limonene", noGreekLabel: "Alpha-Limonene", color: "FAD500" },
  aMyrcene: { isTerpene: true, label: "α-Myrcene", noGreekLabel: "Alpha-Myrcene", color: "FFBF34" },
  aPhellandrene: { isTerpene: true, label: "α-Phellandrene", noGreekLabel: "Alpha-Phellandrene", color: "FE2C71" },
  aPinene: { isTerpene: true, label: "α-Pinene", noGreekLabel: "Alpha-Pinene", color: "0B934B" },
  aTerpinene: { isTerpene: true, label: "α-Terpinene", noGreekLabel: "Alpha-Terpinene", color: "0B934B" },
  aTerpineol: { isTerpene: true, label: "α-Terpineol", noGreekLabel: "Alpha-Terpineol", color: "26C7EE" },
  bCaryophyllene: { isTerpene: true, label: "β-Caryophyllene", noGreekLabel: "Beta-Caryophyllene", color: "420303" },
  bEudesmol: { isTerpene: true, label: "β-Eudesmol", noGreekLabel: "Beta-Eudesmol", color: "876155" },
  bFarnesene: { isTerpene: true, label: "β-Farnesene", noGreekLabel: "Beta-Farnesene", color: "FE58D2" },
  bHumulene: { isTerpene: true, label: "β-Humulene", noGreekLabel: "Beta-Humulene", color: "7567F0" },
  bMyrcene: { isTerpene: true, label: "β-Myrcene", noGreekLabel: "Beta-Myrcene", color: "FFBF34" },
  bOcimene: { isTerpene: true, label: "β-Ocimene", noGreekLabel: "Beta-Ocimene", color: "FA3712" },
  bPinene: { isTerpene: true, label: "β-Pinene", noGreekLabel: "Beta-Pinene", color: "2BD4BF" },
  bTerpinene: { isTerpene: true, label: "β-Terpinene", noGreekLabel: "Beta-Terpinene", color: "FAD500" },
  borneol: { isTerpene: true, label: "Borneol", noGreekLabel: "Borneol" },
  camphene: { isTerpene: true, label: "Camphene", noGreekLabel: "Camphene", color: "3EB489" },
  carene: { isTerpene: true, label: "Carene", noGreekLabel: "Carene", color: "FE6702" },
  caryophylleneOxide: { isTerpene: true, label: "Caryophyllene Oxide", noGreekLabel: "Caryophyllene Oxide", color: "78081C" },
  cisNerolidol: { isTerpene: true, label: "cis-Nerolidol", noGreekLabel: "cis-Nerolidol", color: "EAAB66" },
  cisOcimene: { isTerpene: true, label: "cis-Ocimene", noGreekLabel: "cis-Ocimene", color: "FA3712" },
  dLimonene: { isTerpene: true, label: "d-Limonene", noGreekLabel: "d-Limonene", color: "FAD500" },
  eucalyptol: { isTerpene: true, label: "Eucalyptol", noGreekLabel: "Eucalyptol" },
  farnesol: { isTerpene: true, label: "Farnesol", noGreekLabel: "Farnesol", color: "bff7a1" },
  fenchol: { isTerpene: true, label: "Fenchol", noGreekLabel: "Fenchol", color: "2BD4BF" },
  geraniol: { isTerpene: true, label: "Geraniol", noGreekLabel: "Geraniol" },
  gTerpinene: { isTerpene: true, label: "γ-Terpinene", noGreekLabel: "Gamma-Terpinene", color: "FAD500" },
  guaiol: { isTerpene: true, label: "Guaiol", noGreekLabel: "Guaiol", color: "C184BD" },
  limonene: { isTerpene: true, label: "Limonene", noGreekLabel: "Limonene", color: "FAD500" },
  linalool: { isTerpene: true, label: "Linalool", noGreekLabel: "Linalool", color: "B925AC" },
  myrcene: { isTerpene: true, label: "Myrcene", noGreekLabel: "Myrcene", color: "FFBF34" },
  nerolidol: { isTerpene: true, label: "Nerolidol", noGreekLabel: "Nerolidol", color: "EAAB66" },
  nerol: { isTerpene: true, label: "Nerol", noGreekLabel: "Nerol" },
  ocimene: { isTerpene: true, label: "Ocimene", noGreekLabel: "Ocimene", color: "FE6702" },
  otherTerpenes: { isTerpene: true, label: "Other Terpenes", noGreekLabel: "Other Terpenes", color: "77aa77" },
  sabinene: { isTerpene: true, label: "Sabinene", noGreekLabel: "Sabinene" },
  sabineneHydrate: { isTerpene: true, label: "Sabinene Hydrate", noGreekLabel: "Sabinene Hydrate" },
  squalene: { isTerpene: true, label: "Squalene", noGreekLabel: "Squalene", color: "18dbbe" },
  terpineol: { isTerpene: true, label: "Terpineol", noGreekLabel: "Terpineol", color: "26C7EE" },
  terpinolene: { isTerpene: true, label: "Terpinolene", noGreekLabel: "Terpinolene", color: "FE2C71" },
  transNerolidol: { isTerpene: true, label: "trans-Nerolidol", noGreekLabel: "trans-Nerolidol", color: "EAAB66" },
  valencene: { isTerpene: true, label: "Valencene", noGreekLabel: "Valencene", color: "FF9B9B" },
  totalTerpenes: { isTerpene: true, isDerivative: true, label: "Total Terpenes", noGreekLabel: "Total Terpenes", color: "0B934B" },
};

// Aliases
terpenesMeta.cOcimene = terpenesMeta.cisOcimene;
terpenesMeta.aTerpinolene = terpenesMeta.terpinolene;
terpenesMeta.tNerolidol = terpenesMeta.transNerolidol;
terpenesMeta.aBisabolbol = terpenesMeta.aBisabolol; // legacy typo

// ============================================================================
// Combined
// ============================================================================

const ingredientsMeta: Record<string, IngredientMeta> = {
  ...cannabinoidsMeta,
  ...terpenesMeta,
};

// ============================================================================
// Export as MetrcGlobals shape
// ============================================================================

export const metrcGlobalsData: MetrcGlobals = {
  ingredients: ingredientsMeta,
  cannabinoids: cannabinoidsMeta,
  terpenes: terpenesMeta,
};

// Additional exports for direct access
export { cannabinoidsMeta, terpenesMeta, ingredientsMeta };
export const ingredientKeys = Object.keys(ingredientsMeta);
export const cannabinoidKeys = Object.keys(cannabinoidsMeta);
export const terpeneKeys = Object.keys(terpenesMeta);
